import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useToast } from 'primevue/usetoast'
import useSWRV from 'swrv'
import axios from '@/lib/API/client'
import { fetchFresh, reloadFresh } from '@/lib/API/fetcher'
import store from '@/store'
import {
    ACCEPT_ALL_TOAST_KEY,
    ACCEPT_ALL_URL,
    BOX_RECEIVED,
    BOX_SENT,
    answerParams,
    errorKey,
    invitationsUrl,
    needsRole,
    toastKey,
} from '@/lib/invitations'
import { answeredSharingUrl } from '@/lib/sharing'

// S5: the invitations and access requests of the signed-in user (Paramètres › Invitations),
// and the answers to them, given there, in the « Partage » block of their object, or where
// the reader asked for access. Always read fresh from the API (fetchFresh).
const RECEIVED_URL = invitationsUrl(BOX_RECEIVED)
const SENT_URL = invitationsUrl(BOX_SENT)

// The red badge is the size of the received box (GET /user: pending_invitations_count):
// each read of that box sets it, without another GET /user. Not offline (null), and
// never onto someone who signed in meanwhile.
const fetchReceived = async (key) => {
    const userId = store.state.auth?.user?.id
    const list = await fetchFresh(key)
    const user = store.state.auth?.user
    const same = user && String(user.id) === String(userId)
    if (same && Array.isArray(list) && user.pending_invitations_count !== list.length)
        await store.dispatch('auth/setUser', { ...user, pending_invitations_count: list.length })
    return list
}

const refresh = () => Promise.all([reloadFresh(RECEIVED_URL, fetchReceived), reloadFresh(SENT_URL)])

export default () => {
    const { data: received, error: receivedError } = useSWRV(RECEIVED_URL, fetchReceived)
    const { data: sent, error: sentError } = useSWRV(SENT_URL, fetchFresh)

    return { received, sent, receivedError, sentError, refresh }
}

// job: required to accept a request to join a horse (the owner chooses the role).
// params: a batch is cancelled or sent again whole (answerParams).
const ANSWERS = {
    accepted: (id, { job }) => axios.post(`/invitations/${id}/accept`, job ? { job } : {}),
    refused: (id) => axios.post(`/invitations/${id}/refuse`),
    cancelled: (id, { params }) => axios.delete(`/invitations/${id}`, { params }),
    resent: (id, { params }) => axios.post(`/invitations/${id}/resend`, null, { params }),
}

// Whatever shows the rows is reloaded after every answer, even a failed one (a 409 means
// someone else answered first, and the row must leave the list), wherever it was given:
// both boxes with the badge, and the « Partage » block of its object (sharing).
const reloadingAfter = async (write, sharing = null) => {
    try {
        await write()
    } finally {
        await Promise.all([refresh(), sharing && reloadFresh(sharing)]).catch(() => undefined)
    }
}

// invitation: a row, or { id, kind } (a requester withdrawing their own request).
export const answerInvitation = (invitation, answer, job) =>
    reloadingAfter(
        () => ANSWERS[answer](invitation.id, { job, params: answerParams(invitation) }),
        answeredSharingUrl(invitation, answer)
    )

// « Tout accepter » (the received box).
export const acceptAllInvitations = () => reloadingAfter(() => axios.post(ACCEPT_ALL_URL))

// Accepting, refusing, cancelling or sending again a row, and its toast.
// onAnswered(invitation, answer): once the API took the answer.
export const useInvitationAnswers = (onAnswered = () => undefined) => {
    const { t } = useI18n()
    const toast = useToast()

    // One answer at a time per row (or for « Tout accepter »): a second tap would only meet a 409.
    const busy = new Set()
    const run = async (key, write, summaryKey, done = () => undefined) => {
        if (busy.has(key)) return
        busy.add(key)
        try {
            await write()
            toast.add({ severity: 'success', summary: t(summaryKey), life: 3000 })
            done()
        } catch (error) {
            const errorText = errorKey(error?.response?.status)
            toast.add({ severity: 'warn', summary: errorText ? t(errorText) : error?.message, life: 3000 })
        } finally {
            busy.delete(key)
        }
    }
    const answer = (invitation, reply, job = null) =>
        run(
            invitation.id,
            () => answerInvitation(invitation, reply, job),
            toastKey(invitation, reply),
            () => onAnswered(invitation, reply)
        )

    // A request to join a horse: the owner chooses the role first (AcceptRoleModal).
    const roleModalOpen = ref(false)
    const roleInvitation = ref(null)

    const accept = (invitation) => {
        if (!needsRole(invitation)) return answer(invitation, 'accepted')
        roleInvitation.value = invitation
        roleModalOpen.value = true
    }
    const fromRoleModal = (reply, job) => {
        const invitation = roleInvitation.value
        if (!invitation) return
        roleModalOpen.value = false
        return answer(invitation, reply, job)
    }

    return {
        accept,
        refuse: (invitation) => answer(invitation, 'refused'),
        cancel: (invitation) => answer(invitation, 'cancelled'),
        resend: (invitation) => answer(invitation, 'resent'),
        acceptAll: () => run(ACCEPT_ALL_URL, acceptAllInvitations, ACCEPT_ALL_TOAST_KEY),
        roleModalOpen,
        roleInvitation,
        acceptWithRole: (job) => fromRoleModal('accepted', job),
        refuseFromModal: () => fromRoleModal('refused'),
    }
}
